import * as Helper from "@/utils/helpers";
import * as Server from "@/utils/api";
import { acceptHMRUpdate, defineStore } from 'pinia'
import { useOrganizationStore } from "./organization";

export const useAccountStore = defineStore({
    id: 'account',
    state: () => {
        return {
            token: "",
            account: {},
            betaFeatures: [],
            isImpersonating: false,
            impersonator: null,
            onboarding: null
        }
    },
    getters: {
        getId(state) {
            return state.account.id;
        },
        isLoggedIn(state) {
            return !!state.token;
        },
        // A professional/organizer user (account type, self-label, or a
        // pro/admin/ambassador role). Used to surface pro-only UI such as the
        // event Share Link (LA-182).
        isProfessional(state) {
            const a = state.account || {};
            if (a.account_type === 'professional' || a.is_professional) {
                return true;
            }
            const proRoles = ['level-1', 'level-2', 'level-3', 'level-5'];
            return (a.roles || []).some((r) => proRoles.includes(r.name));
        },
        hasAccessToFeature(state) {
            return (feature) => {
                if (!state.betaFeatures) {
                    return false;
                }
                let hasFeature = false;
                state.betaFeatures.forEach((f) => {
                    if (f === feature) {
                        hasFeature = true;
                    }
                });
                return hasFeature;
            }
        },
        accountRoles(state) {
            return state.account.roles;
        },
        hasPermission(state) {
            return (permission) => {
                if (!state.account.permissions) {
                    return false;
                }
                let hasPerm = false;
                state.account.permissions.forEach((perm) => {
                    if (perm.name === permission) {
                        hasPerm = true;
                    }
                });
                return hasPerm;
            }
        },
        hasRole(state) {
            return (role, organization = null) => {
                let hasRole = false;

                if(organization) {
                    if (!state.account.user_organizations) {
                        return false;
                    }

                    state.account.user_organizations.forEach((o) => {
                        if (organization.id === o.organization_id && o.role === role) {
                            hasRole = true;
                        }
                    });
                }
                else {
                    if (!state.account.roles) {
                        return false;
                    }

                    state.account.roles.forEach((r) => {
                        if (r.name === role) {
                            hasRole = true;
                        }
                    });
                }
                return hasRole;
            }
        },
    },
    actions: {
        logout() {
            this.token = "";
            this.isImpersonating = false;
            this.impersonator = null;
            localStorage.removeItem('token');
            localStorage.removeItem('admin_token');
            localStorage.removeItem('selected_organisation');
            this.getBetaFeatures();
        },
        async startImpersonation(userId) {
            const data = await Server.impersonateUser(userId);
            if (data.status) {
                localStorage.setItem('admin_token', localStorage.getItem('token'));
                return this.login(data.token);
            }
            return data;
        },
        async stopImpersonation() {
            const data = await Server.stopImpersonating();
            if (data.status) {
                localStorage.removeItem('admin_token');
                return this.login(data.token);
            }
            return data;
        },
        login(token) {
            localStorage.setItem('token', token);
            return this.validateAccountToken();
        },
        setBetaFeatures(betaFeatures) {
            this.betaFeatures = betaFeatures;
        },
        setAccount(account) {
            this.account = account;
        },
        permissions(permissions) {
            this.account.permissions = permissions;
        },
        async getBetaFeatures() {
            return await Server.call("beta-features", "GET").then((data) => {
                this.setBetaFeatures(data);
                return data;
            }).catch((error) => {
                console.error("Error during server call:", error);
                return null;
            });
        },
        async validateAccountToken() {
            this.token = localStorage.getItem('token');

            return await Server.call("auth/whoami", "POST").then((data) => {
                Helper.setLanguage(data?.user?.preferred_language ?? 'en');

                if (data?.blocked)
                {
                    this.$router.push({
                        name: 'blocked',
                        params: { user: '', reason: data.reason }
                    })
                    return;
                }

                if (
                    typeof data.user.favorites !== 'undefined' &&
                    data.user.favorites !== null &&
                    data.user.favorites.length > 0
                ) {
                    let favorites = {};
                    data.user.favorites.forEach((favorite) => {
                        if (!favorites[favorite.type]) {
                            favorites[favorite.type] = [];
                        }
                        favorites[favorite.type].push(favorite.mode + '_' + favorite.favoritable_id);
                    });
                    data.user.favorites = favorites;
                }
                this.setAccount(data.user);
                this.permissions(data.user_permissions);
                this.onboarding = data.onboarding || null;
                this.isImpersonating = !!data.is_impersonating;
                this.impersonator = data.impersonator || null;
                const organizationStore = useOrganizationStore();
                organizationStore.getManagableOrganizations();
                this.getBetaFeatures();
                if (!data.user.id) {
                    this.logout();
                }
                return data.user;
            }).catch((error) => {
                console.error("Error during server call:", error);
                this.logout();
                return null;
            });
        }
    },
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAccountStore, import.meta.hot))
}